import type {
  FormField,
  Form,
  FieldsOverrides,
} from '@wix/ambassador-forms-v4-form/types';

import type { FormOverrides } from '../types';

import {
  getNestedFormArray,
  getNestedFormsByTarget,
} from './apply-nested-forms';

export function applyFormOverrides(
  form: Form,
  overrides: FormOverrides,
  recursive = true,
): Form {
  const nestedFormsByTarget = getNestedFormsByTarget(form);

  const fields = (form.fields ?? []).map((field) => {
    const override = getOverride({
      id: field.id,
      target: field.target,
      overrides,
    });

    if (!override) {
      return field;
    }

    if (field.target && field.nestedFormId && recursive) {
      const nestedForm = nestedFormsByTarget?.[field.target];
      if (nestedForm) {
        nestedFormsByTarget[field.target] = applyFormOverrides(
          nestedForm,
          override as FormOverrides,
          false,
        );
      }
      return {
        ...field,
        ...pick(override, 'hidden'),
        ...pick(override, 'readOnly'),
        view: {
          ...field.view,
          ...pick(override, 'label'),
          ...pick(override, 'disabled'),
        },
        validation: {
          ...field.validation,
          ...pick(override, 'required'),
        },
      };
    }

    let validation = field.validation;
    let options = {};
    if (Array.isArray(override.countries)) {
      validation = {
        ...validation,
        string: {
          ...validation?.string,
          enum: override.countries,
        },
      };
      options = override.countries
        ? {
            options: override.countries?.map((country) => ({ value: country })),
          }
        : {};
    }

    if (field.view?.fieldType === 'MULTILINE_ADDRESS') {
      return applyMultilineAddressOverrides({
        field,
        override: override as FormOverrides,
      });
    }

    return {
      ...field,
      ...pick(override, 'hidden'),
      ...pick(override, 'readOnly'),
      view: {
        ...field.view,
        ...pick(override, 'label'),
        ...pick(override, 'autocomplete'),
        ...pick(override, 'disabled'),
        ...(override.hasOwnProperty('callingCountryCode')
          ? { defaultCountry: override.callingCountryCode }
          : {}),
        ...options,
      },
      validation: {
        ...validation,
        ...pick(override, 'required'),
      },
    };
  });

  const nestedForms = nestedFormsByTarget
    ? getNestedFormArray(nestedFormsByTarget)
    : undefined;

  return {
    ...form,
    fields,
    ...(nestedForms ? { nestedForms } : {}),
  };
}

function pick<Type extends {}>(override: Type, key: keyof Type) {
  return override.hasOwnProperty(key) ? { [key]: override[key] } : {};
}

const getOverride = ({
  target,
  id,
  overrides,
}: {
  target?: string | null;
  id?: string;
  overrides: FormOverrides;
}) => {
  if (target && target in overrides) {
    return overrides[target];
  }
  if (id && id in overrides) {
    return overrides[id];
  }
  return undefined;
};

const applyAddressFieldOverride = (
  fieldName: string,
  override: FormOverrides,
  multilineAddressOptionsFields: FieldsOverrides,
  multilineAddressFieldSettings: Record<string, any>,
) => {
  const fieldOverride = override[fieldName];

  if (!fieldOverride) {
    return {
      multilineAddressOptionsFields,
      multilineAddressFieldSettings,
    };
  }

  return {
    multilineAddressOptionsFields: {
      ...multilineAddressOptionsFields,
      ...(fieldOverride.required !== undefined
        ? {
            [fieldName]: {
              ...multilineAddressOptionsFields?.[
                fieldName as keyof FieldsOverrides
              ],
              required: fieldOverride.required,
            },
          }
        : {}),
    },
    multilineAddressFieldSettings: {
      ...multilineAddressFieldSettings,
      [fieldName]: {
        ...multilineAddressFieldSettings?.[fieldName],
        show: !fieldOverride.hidden,
      },
    },
  };
};

const ADDRESS_FIELD_NAMES = [
  'addressLine',
  'addressLine2',
  'streetName',
  'streetNumber',
  'city',
  'postalCode',
  'subdivision',
  'country',
  'apartment',
] as const;

const applyMultilineAddressOverrides = ({
  field,
  override,
}: {
  field: FormField;
  override: FormOverrides;
}) => {
  const finalState = ADDRESS_FIELD_NAMES.reduce(
    (state, fieldName) =>
      applyAddressFieldOverride(
        fieldName,
        override,
        state.multilineAddressOptionsFields ?? {},
        state.multilineAddressFieldSettings ?? {},
      ),
    {
      multilineAddressOptionsFields:
        field.validation?.predefined?.multilineAddressOptions?.fields,
      multilineAddressFieldSettings: field.view?.fieldSettings,
    },
  );

  const autocompleteEnabled =
    (override.addressLine?.autocomplete || override.streetName?.autocomplete) ??
    true;

  const allowedCountries =
    Array.isArray(override.country?.countries) &&
    override.country?.countries?.length > 0
      ? { allowedCountries: override.country?.countries }
      : {};

  const multilineAddressOptions = {
    ...field.validation?.predefined?.multilineAddressOptions,
    fields: {
      ...field.validation?.predefined?.multilineAddressOptions?.fields,
      ...finalState.multilineAddressOptionsFields,
    },
    ...allowedCountries,
  };

  return {
    ...field,
    view: {
      ...field.view,
      fieldSettings: {
        ...field.view?.fieldSettings,
        ...finalState.multilineAddressFieldSettings,
      },
      autocompleteEnabled,
    },
    validation: {
      ...field.validation,
      predefined: {
        ...field.validation?.predefined,
        multilineAddressOptions,
      },
    },
  };
};
